import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, CircularProgress } from '@mui/material';

type Props = {
  onDownloadTemplate: () => void,
  onClose: () => void,
  onImported: () => void,
};

export default function FansImportDialog({ onDownloadTemplate, onClose, onImported }: Props) {
  const [isUploading, setIsUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const resetState = () => {
    setIsUploading(false);
    setSuccess(false);
    setErrorText(null);
  };

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    setSuccess(false);
    setErrorText(null);

    try {
      const formData = new FormData();
      formData.append('File', file);

      const res = await fetch('/api/my-fans/import', {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        setSuccess(true);
        onImported?.();
      } else if (res.status === 400) {
        const text = await res.text();
        setErrorText(text || 'Import failed with validation errors.');
      } else {
        const text = await res.text();
        setErrorText(text || `Unexpected error (status ${res.status}).`);
      }
    } catch (e: any) {
      setErrorText(e?.message || 'Network error while uploading file.');
    } finally {
      setIsUploading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file: File = acceptedFiles[0];
    if (file) uploadFile(file);
  }, []);

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject
  } = useDropzone({
    accept: { 'text/csv': [] },
    onDrop,
    maxFiles: 1
  });

  const style = useMemo(() => ({
    ...styles.baseStyle,
    ...(isFocused ? styles.focusedStyle : {}),
    ...(isDragAccept ? styles.acceptStyle : {}),
    ...(isDragReject ? styles.rejectStyle : {})
  }), [
    isFocused,
    isDragAccept,
    isDragReject
  ]);

  const showErrors = !!errorText && !isUploading && !success;

  return (
    <Dialog open={true} maxWidth={'md'} fullWidth onClose={onClose}>
      <DialogTitle>
        Import Fans
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 180 }}>
        {isUploading && (
          <Box sx={styles.centerBox}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Uploading and validating...</Typography>
          </Box>
        )}

        {!isUploading && success && (
          <Box sx={styles.centerBox}>
            <Typography variant="h6" color="success.main">Import Successful</Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Your fans list has been updated.
            </Typography>
          </Box>
        )}

        {showErrors && (
          <Box sx={styles.errorContainer}>
            <Typography variant="h6" color="error.main" sx={{ mb: 1, textAlign: 'center' }}>Import Failed</Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center' }}>
              Make sure your CSV file matches the required format, remove blank lines for better error tracking. See the template for details.
              Fix the following rows and try to reupload again.
            </Typography>
            <Box component="pre" sx={styles.errorPre}>
              {errorText}
            </Box>
          </Box>
        )}

        {!isUploading && !success && !errorText && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Prepare your CSV file:</Typography>
              <Box component="ul" sx={{ fontSize: '0.875rem', pl: 3, m: 0 }}>
                <li>Download the template and enter your data following the provided instructions.</li>
                <li>Keep the exact column order and ensure the data matches the required columns.</li>
                <li>Phone numbers must follow the E.164 format (e.g., +15615899741).</li>
                <li>Check that your data is properly aligned and not shifted across columns.</li>
                <li>Remove any blank rows to improve error tracking if the import fails.</li>
              </Box>
            </Box>
            <div {...getRootProps({ style })}>
              <input {...getInputProps()} />
              <p>Drag and drop a CSV file here, or click to select it.</p>
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={onDownloadTemplate}
          disabled={isUploading}
        >
          Download Template
        </Button>

        {!isUploading && (success || errorText) && (
          <Button
            variant="outlined"
            color="primary"
            onClick={resetState}
          >
            Try Again
          </Button>
        )}

        <Button
          variant="text"
          color="inherit"
          onClick={onClose}
          sx={{ width: 100 }}
          disabled={isUploading}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const styles = {
  baseStyle: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
    justifyContent: 'center',
    minHeight: 120
  },
  focusedStyle: { borderColor: '#2196f3' },
  acceptStyle: { borderColor: '#00e676' },
  rejectStyle: { borderColor: '#ff1744' },
  centerBox: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: 120
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  errorPre: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    background: '#f8f8f8',
    border: '1px solid #eee',
    borderRadius: '6px',
    padding: '12px',
    maxHeight: 240,
    overflow: 'auto',
    fontFamily: 'monospace',
    fontSize: '12px',
  }
};
